import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "../lib/api";

const LangCtx = createContext(null);

const FLAGS = {
  it: "🇮🇹", en: "🇬🇧", de: "🇩🇪", fr: "🇫🇷", es: "🇪🇸", pt: "🇵🇹",
};
const NAMES = {
  it: "Italiano", en: "English", de: "Deutsch", fr: "Français", es: "Español", pt: "Português",
};

const FALLBACK_RATES = {
  EUR: { rate: 1.0, symbol: "€", position: "before", decimals: 2 },
  USD: { rate: 1.08, symbol: "$", position: "before", decimals: 2 },
  GBP: { rate: 0.84, symbol: "£", position: "before", decimals: 2 },
  CHF: { rate: 0.95, symbol: "CHF ", position: "before", decimals: 2 },
};
const FALLBACK_LANG_DEFAULT = { it: "EUR", de: "EUR", fr: "EUR", es: "EUR", pt: "EUR", en: "GBP" };

export function LangProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("ti_lang") || "it");
  const [dict, setDict] = useState({});
  const [supported, setSupported] = useState(["it", "en", "de", "fr", "es", "pt"]);
  const [geoChecked, setGeoChecked] = useState(() => !!localStorage.getItem("ti_geo"));
  const [suggestion, setSuggestion] = useState(null);

  // Currency
  const [rates, setRates] = useState(FALLBACK_RATES);
  const [langDefault, setLangDefault] = useState(FALLBACK_LANG_DEFAULT);
  const [supportedCurrencies, setSupportedCurrencies] = useState(["EUR", "USD", "GBP", "CHF"]);
  const [currency, setCurrencyState] = useState(() => localStorage.getItem("a3k_currency") || null); // null = follow lang

  const load = useCallback(async (l) => {
    try {
      const r = await api.get(`/translations/${l}`);
      setDict(r.data.dict || {});
      setSupported(r.data.supported || ["it", "en", "de", "fr", "es", "pt"]);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { load(lang); localStorage.setItem("ti_lang", lang); }, [lang, load]);

  // Fetch FX rates once on mount
  useEffect(() => {
    api.get("/currency/rates").then((r) => {
      const d = r.data || {};
      if (d.rates) setRates(d.rates);
      if (d.lang_default) setLangDefault(d.lang_default);
      if (d.supported) setSupportedCurrencies(d.supported);
    }).catch(() => { /* keep fallback */ });
  }, []);

  useEffect(() => {
    if (geoChecked) return;
    api.get("/geo/detect").then((r) => {
      const d = r.data || {};
      const userLang = localStorage.getItem("ti_lang");
      if (!userLang && d.lang && d.lang !== lang) {
        setLang(d.lang);
      } else if (userLang && d.lang && d.lang !== userLang) {
        setSuggestion({ country: d.country, lang: d.lang });
      }
      // Currency auto-pick from geo if user has no override
      if (!localStorage.getItem("a3k_currency") && d.currency && rates[d.currency]) {
        setCurrencyState(d.currency);
      }
      localStorage.setItem("ti_geo", "1");
      setGeoChecked(true);
    }).catch(() => setGeoChecked(true));
    // eslint-disable-next-line
  }, []);

  const t = (key, fallback) => dict[key] || fallback || key;

  // Effective currency: explicit choice > lang default > EUR
  const effectiveCurrency = useMemo(() => {
    if (currency && rates[currency]) return currency;
    return langDefault[lang] || "EUR";
  }, [currency, lang, langDefault, rates]);

  const setCurrency = (c) => {
    if (!rates[c]) return;
    setCurrencyState(c);
    localStorage.setItem("a3k_currency", c);
  };

  // Convert an EUR amount to the effective currency, formatted with locale
  const formatPrice = useCallback((eurAmount, opts = {}) => {
    const cfg = rates[effectiveCurrency] || rates.EUR;
    const value = (Number(eurAmount) || 0) * cfg.rate;
    const decimals = opts.decimals !== undefined ? opts.decimals : cfg.decimals;
    const localeMap = { it: "it-IT", en: "en-GB", de: "de-DE", fr: "fr-FR", es: "es-ES", pt: "pt-PT" };
    const locale = localeMap[lang] || "en-GB";
    const formatted = value.toLocaleString(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    return cfg.position === "after" ? `${formatted}${cfg.symbol}` : `${cfg.symbol}${formatted}`;
  }, [rates, effectiveCurrency, lang]);

  return (
    <LangCtx.Provider value={{
      lang, setLang, t, dict, supported, flags: FLAGS, names: NAMES, suggestion, setSuggestion,
      currency: effectiveCurrency, setCurrency, supportedCurrencies, rates, formatPrice,
    }}>
      {children}
    </LangCtx.Provider>
  );
}

export const useLang = () => useContext(LangCtx);
